import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Modal, Button, FormControl, ControlLabel, FormGroup } from 'react-bootstrap';
import moment from 'moment';
import FormInput from '../../components/FormInput';
import * as adminActionCreator from '../../actions/admin';
import { RPM_CONSENT_TYPE_OPTIONS } from '../../constants/rpmEnrollmentMocks';
import { StateData } from '../../constants/other';
import { getRpmDeviceClass, getRpmDeviceTypeLabel } from '../../constants/other';
import {
    buildRpmEnrollRequest,
    formatValidatedAddressLabel,
    normalizeValidatedAddress,
} from '../../utils/rpmEnrollmentHelpers';
import {
    fetchRpmDevices,
    validateRpmShippingAddress,
} from '../../services/rpmEnrollmentService';
import { numberToPhone } from '../../utils';
import ScheduleNextCallModal from './ScheduleNextCallModal';
import RpmAddonDeclineModal from './RpmAddonDeclineModal';

const STEP_SUFFIX_LABELS = ['RPM Device', 'Shipping', 'Confirm', 'Schedule'];

const boxStyle = {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: 4,
    padding: 12,
    marginBottom: 16,
};

const selectedDeviceBoxStyle = {
    background: 'rgb(240,249,243)',
    border: '1px solid rgb(109,173,130)',
    borderRadius: 4,
    padding: 12,
    marginTop: 12,
};

class RpmDualEnrollmentModal extends React.Component {
    constructor(props) {
        super(props);
        this.inputMobilePhn = props.patient && props.patient.mobile ? props.patient.mobile : '';
        this.state = this.getInitialState(props.patient);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.show && !this.props.show) {
            this.inputMobilePhn = nextProps.patient && nextProps.patient.mobile ? nextProps.patient.mobile : '';
            this.setState(this.getInitialState(nextProps.patient));
        }
    }

    getInitialState(patient) {
        const mobile = patient && patient.mobile ? patient.mobile : '';
        return {
            step: 1,
            showComplete: false,
            rpmDeclined: false,
            ccmConsentType: '',
            ccmConsentChecked: false,
            mobile,
            okToText: patient && patient.ok_to_text != null ? patient.ok_to_text : null,
            address1: patient && patient.address1 ? patient.address1 : '',
            address2: patient && patient.address2 ? patient.address2 : '',
            city: patient && patient.city ? patient.city : '',
            state: patient && patient.state ? patient.state : '',
            zip_code: patient && patient.zip_code ? patient.zip_code : '',
            connectionType: '',
            devices: [],
            devicesLoading: false,
            devicesError: '',
            selectedDeviceId: '',
            shipping: {
                address1: patient && patient.address1 ? patient.address1 : '',
                address2: patient && patient.address2 ? patient.address2 : '',
                city: patient && patient.city ? patient.city : '',
                state: patient && patient.state ? patient.state : '',
                zip_code: patient && patient.zip_code ? patient.zip_code : '',
            },
            addressVerified: false,
            addressValidationMessage: '',
            addressValidationError: '',
            validatingAddress: false,
            validatedAddresses: [],
            selectedValidatedAddressIndex: null,
            rpmConsentChecked: false,
            rpmConsentType: '',
            submitting: false,
            submitError: '',
            workItemText: '',
            scheduleNextCallDate: null,
            scheduleNextCallSlot: null,
            showRpmDeclineModal: false,
            enrollmentSummary: null,
        };
    }

    getSelectedDevice() {
        return this.state.devices.find((device) => device.id === this.state.selectedDeviceId);
    }

    getDeviceTypeLabel() {
        const { patient, rpmDeviceTypes } = this.props;
        return getRpmDeviceTypeLabel(rpmDeviceTypes, patient && patient.rpm_device_type_id) || '—';
    }

    getConnectionLabel() {
        if (this.state.connectionType === 'bluetooth') {
            return 'Bluetooth';
        }
        if (this.state.connectionType === 'cellular') {
            return 'Cellular';
        }
        return '';
    }

    getRpmDiagnosisDisplay() {
        const { patient } = this.props;
        if (!patient || !patient.rpm_icd10code) {
            return '—';
        }
        return [patient.rpm_icd10code, patient.rpm_icd10code_desc].filter(Boolean).join(' — ');
    }

    getProgramShortLabel() {
        const { patient } = this.props;
        return patient && patient.program_type === 'apcm' ? 'APCM' : 'CCM';
    }

    getProgramLabel() {
        const { patient } = this.props;
        if (!patient) {
            return '—';
        }
        if (patient.program_type === 'apcm') {
            return patient.apcm_eligible_level
                ? `APCM Level ${patient.apcm_eligible_level}`
                : 'APCM';
        }
        return 'CCM';
    }

    getStepLabels() {
        return [this.getProgramShortLabel(), ...STEP_SUFFIX_LABELS];
    }

    getTextClinicalEnabled() {
        const { patient } = this.props;
        return !!(patient && patient.provider && patient.provider.text_clinical);
    }

    getMobileValue() {
        if (this.mobileInputRef && this.mobileInputRef.getValue) {
            return this.mobileInputRef.getValue();
        }
        return this.state.mobile || this.inputMobilePhn;
    }

    getBluetoothMobileValue() {
        if (this.bluetoothMobileInputRef && this.bluetoothMobileInputRef.getValue) {
            return this.bluetoothMobileInputRef.getValue();
        }
        return this.state.mobile;
    }

    goToStep(step) {
        this.setState({ step, submitError: '' });
    }

    onBack() {
        const { step, rpmDeclined } = this.state;
        if (step === 5) {
            this.goToStep(4);
        } else if (step === 4) {
            this.goToStep(rpmDeclined ? 1 : 3);
        } else if (step === 3) {
            this.goToStep(2);
        } else if (step === 2) {
            this.goToStep(1);
        }
    }

    onContinueFromStep1() {
        const textClinical = this.getTextClinicalEnabled();
        const { ccmConsentType, ccmConsentChecked, okToText } = this.state;

        if (!ccmConsentType || !ccmConsentChecked) {
            return;
        }
        if (textClinical && okToText == null) {
            return;
        }
        if (!this.mobileInputRef || !this.mobileInputRef.validate()) {
            return;
        }

        const mobile = this.getMobileValue();
        const shipping = {
            address1: this.state.address1,
            address2: this.state.address2,
            city: this.state.city,
            state: this.state.state,
            zip_code: this.state.zip_code,
        };

        this.setState({ mobile, shipping }, () => this.goToStep(2));
    }

    onContinueFromStep2() {
        const { selectedDeviceId, connectionType } = this.state;
        if (!selectedDeviceId || !connectionType) {
            return;
        }
        if (connectionType === 'bluetooth') {
            if (!this.bluetoothMobileInputRef || !this.bluetoothMobileInputRef.validate()) {
                return;
            }
            const mobile = this.getBluetoothMobileValue();
            this.setState({ mobile }, () => this.goToStep(3));
            return;
        }
        this.goToStep(3);
    }

    onDeclineRpm() {
        this.setState({ showRpmDeclineModal: true });
    }

    onRpmDeclineBack() {
        this.setState({ showRpmDeclineModal: false });
    }

    onRpmDeclineSuccess() {
        this.setState({ showRpmDeclineModal: false, rpmDeclined: true }, () => this.goToStep(4));
    }

    onConnectionTypeChange(connectionType) {
        const { patient, rpmDeviceTypes } = this.props;
        const deviceClass = getRpmDeviceClass(rpmDeviceTypes, patient && patient.rpm_device_type_id);

        if (!deviceClass) {
            this.setState({
                connectionType,
                devices: [],
                selectedDeviceId: '',
                devicesError: 'Device type is not available for this patient.',
            });
            return;
        }

        this.setState({
            connectionType,
            selectedDeviceId: '',
            devices: [],
            devicesLoading: true,
            devicesError: '',
        }, () => {
            fetchRpmDevices(deviceClass, connectionType)
                .then((devices) => {
                    this.setState({
                        devices,
                        devicesLoading: false,
                        devicesError: devices.length ? '' : 'No devices available for this connection type.',
                    });
                })
                .catch(() => {
                    this.setState({
                        devicesLoading: false,
                        devicesError: 'Failed to load devices. Please try again.',
                    });
                });
        });
    }

    onValidateAddress() {
        this.setState({
            validatingAddress: true,
            addressValidationError: '',
            addressValidationMessage: '',
            addressVerified: false,
            validatedAddresses: [],
            selectedValidatedAddressIndex: null,
        });
        validateRpmShippingAddress(this.state.shipping)
            .then((result) => {
                if (result.status === 'verified') {
                    this.setState({
                        validatingAddress: false,
                        addressVerified: true,
                        shipping: result.shipping,
                        validatedAddresses: [],
                        selectedValidatedAddressIndex: null,
                        addressValidationMessage: result.message,
                        addressValidationError: '',
                    });
                } else if (result.status === 'selection_required') {
                    this.setState({
                        validatingAddress: false,
                        addressVerified: false,
                        validatedAddresses: result.addresses,
                        selectedValidatedAddressIndex: null,
                        addressValidationMessage: result.message,
                        addressValidationError: '',
                    });
                } else {
                    this.setState({
                        validatingAddress: false,
                        addressVerified: false,
                        validatedAddresses: [],
                        selectedValidatedAddressIndex: null,
                        addressValidationError: result.error,
                        addressValidationMessage: '',
                    });
                }
            })
            .catch(() => {
                this.setState({
                    validatingAddress: false,
                    addressVerified: false,
                    validatedAddresses: [],
                    selectedValidatedAddressIndex: null,
                    addressValidationError: 'Address validation failed. Please try again.',
                    addressValidationMessage: '',
                });
            });
    }

    onSelectValidatedAddress(index) {
        const address = this.state.validatedAddresses[index];
        if (!address) {
            return;
        }
        this.setState({
            selectedValidatedAddressIndex: index,
            shipping: normalizeValidatedAddress(address),
            addressVerified: true,
            addressValidationMessage: 'Address verified. Deliverable — ready to enroll.',
            addressValidationError: '',
        });
    }

    onShippingChange(field, value) {
        this.setState({
            shipping: {
                ...this.state.shipping,
                [field]: value,
            },
            addressVerified: false,
            addressValidationError: '',
            addressValidationMessage: '',
            validatedAddresses: [],
            selectedValidatedAddressIndex: null,
        });
    }

    onSubmitEnrollment() {
        const { patient, adminActions } = this.props;
        const {
            rpmDeclined,
            ccmConsentType,
            rpmConsentType,
            rpmConsentChecked,
            address1,
            address2,
            city,
            state,
            zip_code,
            okToText,
            shipping,
            connectionType,
            mobile,
        } = this.state;
        const isApcm = patient.program_type === 'apcm';
        const okToTextInt = okToText != null ? parseInt(`${okToText}`, 10) : null;
        const mobileNo = mobile || this.getMobileValue() || '';
        const selectedDevice = this.getSelectedDevice();

        if (!rpmDeclined && (!rpmConsentChecked || !rpmConsentType || !selectedDevice)) {
            return;
        }

        this.setState({ submitting: true, submitError: '' });

        const rpmEnrollRequest = !rpmDeclined ? buildRpmEnrollRequest({
            consentType: rpmConsentType,
            selectedDevice,
            shipping,
            connectionType,
            mobile: connectionType === 'bluetooth' ? mobileNo : null,
        }) : null;

        const ccmConsentLabel = RPM_CONSENT_TYPE_OPTIONS.find((o) => o.value === ccmConsentType);
        const rpmConsentLabel = RPM_CONSENT_TYPE_OPTIONS.find((o) => o.value === rpmConsentType);
        const programLabel = this.getProgramShortLabel();

        adminActions.updateAddress(
            patient,
            address1,
            address2,
            city,
            state,
            zip_code,
            okToTextInt,
            mobileNo,
            () => {
                adminActions.verifyPatient(patient, ccmConsentType, false, isApcm, rpmEnrollRequest)
                    .then(() => adminActions.getPatientDetail(patient.id))
                    .then(() => {
                        const workItemText = rpmDeclined
                            ? `${programLabel} consent (${ccmConsentLabel ? ccmConsentLabel.label : ccmConsentType}) obtained and enrollment confirmed on ${moment().format('MMM D, YYYY')}.`
                            : `${programLabel} consent (${ccmConsentLabel ? ccmConsentLabel.label : ccmConsentType}) and RPM Add-On consent (${rpmConsentLabel ? rpmConsentLabel.label : rpmConsentType}) obtained; enrollment confirmed with Validic on ${moment().format('MMM D, YYYY')}. Device: ${selectedDevice.description}.`;
                        const enrollmentSummary = rpmDeclined ? null : {
                            deviceDescription: selectedDevice.description,
                            participantId: patient.rpm_validic_participant_id || `RPM-${patient.id}`,
                            enrolledTs: moment().format('MMM D, YYYY'),
                        };
                        this.setState({
                            submitting: false,
                            workItemText,
                            enrollmentSummary,
                        }, () => this.goToStep(5));
                    })
                    .catch(() => {
                        this.setState({
                            submitting: false,
                            submitError: 'Enrollment failed. Please try again.',
                        });
                    });
            }
        );
    }

    onCompleteEnrollment() {
        const { patient, adminActions, onComplete } = this.props;
        const { scheduleNextCallSlot } = this.state;

        const finish = () => {
            adminActions.getPatientDetail(patient.id);
            this.setState({ showComplete: true });
            if (onComplete) {
                onComplete({
                    workItemText: this.state.workItemText,
                    rpmEnrolled: !this.state.rpmDeclined,
                    enrollmentSummary: this.state.enrollmentSummary,
                });
            }
        };

        if (scheduleNextCallSlot && scheduleNextCallSlot.id !== undefined) {
            adminActions.saveScheduleNextCall(patient.id, scheduleNextCallSlot.id);
        }
        finish();
    }

    onClose() {
        this.setState(this.getInitialState(this.props.patient));
        this.props.onHide();
    }

    renderStepper() {
        const { step, rpmDeclined } = this.state;
        const stepLabels = this.getStepLabels();
        const visibleSteps = rpmDeclined
            ? stepLabels.filter((label) => label !== 'RPM Device' && label !== 'Shipping')
            : stepLabels;

        return (
            <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0 24px', flexWrap: 'wrap', gap: 8 }}>
                {visibleSteps.map((label, index) => {
                    const stepNumber = stepLabels.indexOf(label) + 1;
                    const isComplete = step > stepNumber;
                    const isActive = step === stepNumber;
                    const circleStyle = {
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        marginRight: 8,
                        background: isComplete ? '#5cb85c' : isActive ? '#337ab7' : '#ccc',
                        color: '#fff',
                    };
                    return (
                        <React.Fragment key={label}>
                            <div style={{ display: 'flex', alignItems: 'center', color: isActive ? '#337ab7' : '#666' }}>
                                <span style={circleStyle}>{isComplete ? '✓' : index + 1}</span>
                                <span style={{ fontWeight: isActive ? 600 : 400 }}>{label}</span>
                            </div>
                            {index < visibleSteps.length - 1 &&
                                <div style={{ flex: 1, minWidth: 24, height: 2, background: step > stepNumber ? '#5cb85c' : '#ddd', margin: '0 8px' }} />
                            }
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    renderStep1() {
        const textClinical = this.getTextClinicalEnabled();
        const { ccmConsentType, ccmConsentChecked, okToText } = this.state;
        const programLabel = this.getProgramShortLabel();

        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Part 1 — primary program consent</div>
                <div style={boxStyle}>
                    <div><span style={{ fontWeight: 600 }}>Program:</span> {this.getProgramLabel()}</div>
                </div>
                <ControlLabel>{programLabel} consent type</ControlLabel>
                <FormControl
                    componentClass="select"
                    value={ccmConsentType}
                    onChange={(e) => this.setState({ ccmConsentType: e.target.value })}
                    style={{ marginBottom: 12 }}
                >
                    <option value="">Select consent type</option>
                    {RPM_CONSENT_TYPE_OPTIONS.map((option) =>
                        <option key={option.value} value={option.value}>{option.label}</option>
                    )}
                </FormControl>
                {textClinical ?
                    <div style={{ marginBottom: 16 }}>
                        <ControlLabel>Text message consent</ControlLabel>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                            {[{
                                value: 1,
                                label: 'Patient consents to receive program-related text messages.',
                            }, {
                                value: 0,
                                label: 'Patient explicitly opts out of text messages.',
                            }, {
                                value: 2,
                                label: 'Patient is not able to receive text messages.',
                            }].map((option) =>
                                <label key={option.value} style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontWeight: 400 }}>
                                    <input
                                        type="radio"
                                        name="dualOkToText"
                                        checked={okToText == option.value}
                                        onChange={() => this.setState({ okToText: option.value })}
                                        style={{ marginRight: 8, marginTop: 3 }}
                                    />
                                    <span>{option.label}</span>
                                </label>
                            )}
                        </div>
                    </div>
                    :
                    <div style={{ color: '#a94442', marginBottom: 16 }}>Clinical Text disabled for Organization</div>
                }
                <FormGroup>
                    <ControlLabel>Mobile phone</ControlLabel>
                    <FormInput
                        type="phone"
                        isRequired
                        ref={(ref) => { this.mobileInputRef = ref; }}
                        placeholder="(XXX) XXX-XXXX"
                        initialValue={this.inputMobilePhn}
                        maxLength="25"
                    />
                </FormGroup>
                <p style={{ fontSize: 13, color: '#666' }}>Mailing address below pre-fills the shipping step.</p>
                <ControlLabel>Address (Line 1)</ControlLabel>
                <FormControl value={this.state.address1} onChange={(e) => this.setState({ address1: e.target.value })} style={{ marginBottom: 12 }} />
                <ControlLabel>Address (Line 2)</ControlLabel>
                <FormControl value={this.state.address2} onChange={(e) => this.setState({ address2: e.target.value })} style={{ marginBottom: 12 }} />
                <ControlLabel>City</ControlLabel>
                <FormControl value={this.state.city} onChange={(e) => this.setState({ city: e.target.value })} style={{ marginBottom: 12 }} />
                <ControlLabel>State</ControlLabel>
                <FormControl componentClass="select" value={this.state.state} onChange={(e) => this.setState({ state: e.target.value })} style={{ marginBottom: 12 }}>
                    <option value="" disabled>-------------------</option>
                    {StateData.map((stateOption) =>
                        <option value={stateOption.id} key={stateOption.id}>{stateOption.name}</option>
                    )}
                </FormControl>
                <ControlLabel>Zip Code</ControlLabel>
                <FormControl value={this.state.zip_code} onChange={(e) => this.setState({ zip_code: e.target.value })} style={{ marginBottom: 12 }} />
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginTop: 8 }}>
                    <input
                        type="checkbox"
                        checked={ccmConsentChecked}
                        onChange={(e) => this.setState({ ccmConsentChecked: e.target.checked })}
                        style={{ marginRight: 8, marginTop: 3 }}
                    />
                    <span>Patient has verbally consented to enroll in {programLabel}.</span>
                </label>
            </div>
        );
    }

    renderBluetoothMobileSection() {
        const { mobile } = this.state;

        return (
            <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                    Confirm or update the patient's mobile phone for bluetooth device enrollment.
                </div>
                <ControlLabel>Mobile Phone</ControlLabel>
                <FormInput
                    key={`dual-rpm-mobile-bluetooth-${mobile}`}
                    type="phone"
                    isRequired
                    ref={(ref) => { this.bluetoothMobileInputRef = ref; }}
                    placeholder="(XXX) XXX-XXXX"
                    initialValue={mobile}
                    maxLength="25"
                />
            </div>
        );
    }

    renderStep2() {
        const { connectionType, devices, devicesLoading, devicesError, selectedDeviceId } = this.state;
        const selectedDevice = this.getSelectedDevice();
        const deviceTypeLabel = this.getDeviceTypeLabel();
        const connectionLabel = this.getConnectionLabel();

        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 2 — select device</div>
                <div style={boxStyle}>
                    <div><span style={{ fontWeight: 600 }}>Approved Device Type:</span> {deviceTypeLabel} · Provider approved</div>
                    <div><span style={{ fontWeight: 600 }}>Diagnosis:</span> {this.getRpmDiagnosisDisplay()}</div>
                </div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>1. How will the device connect?</div>
                <label style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8, cursor: 'pointer' }}>
                    <input type="radio" name="dualConnectionType" checked={connectionType === 'bluetooth'} onChange={() => this.onConnectionTypeChange('bluetooth')} style={{ marginRight: 8, marginTop: 3 }} />
                    <div>
                        Bluetooth <span style={{ marginLeft: 8, background: '#5cb85c', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 10 }}>Preferred</span>
                        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Requires a smartphone, internet access, and a companion app.</div>
                        {connectionType === 'bluetooth' && this.renderBluetoothMobileSection()}
                    </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="radio" name="dualConnectionType" checked={connectionType === 'cellular'} onChange={() => this.onConnectionTypeChange('cellular')} style={{ marginRight: 8, marginTop: 3 }} />
                    <div>
                        Cellular
                        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>For patients without smartphones or internet — ships pre-connected.</div>
                    </div>
                </label>
                {connectionType &&
                    <div style={{ marginTop: 16 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>2. Which device?</div>
                        <FormControl componentClass="select" value={selectedDeviceId} disabled={devicesLoading} onChange={(e) => this.setState({ selectedDeviceId: e.target.value })}>
                            <option value="">{devicesLoading ? 'Loading devices...' : `Select a ${connectionLabel} ${deviceTypeLabel}...`}</option>
                            {devices.map((device) => <option key={device.id} value={device.id}>{device.description}</option>)}
                        </FormControl>
                        {devicesError && <div style={{ color: '#a94442', marginTop: 8 }}>{devicesError}</div>}
                        {selectedDevice &&
                            <div style={selectedDeviceBoxStyle}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>SELECTED DEVICE</div>
                                <div>{selectedDevice.description} · {connectionLabel}</div>
                            </div>
                        }
                    </div>
                }
            </div>
        );
    }

    renderStep3() {
        const { shipping, addressVerified, addressValidationMessage, addressValidationError, validatingAddress, validatedAddresses, selectedValidatedAddressIndex } = this.state;
        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 3 — shipping address</div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                    Pre-populated from the patient record. Override if the device should ship elsewhere, then validate the address.
                </div>
                <ControlLabel>Address Line 1</ControlLabel>
                <FormControl value={shipping.address1} onChange={(e) => this.onShippingChange('address1', e.target.value)} style={{ marginBottom: 12 }} />
                <ControlLabel>Address Line 2</ControlLabel>
                <FormControl value={shipping.address2} placeholder="Apt, suite, unit (optional)" onChange={(e) => this.onShippingChange('address2', e.target.value)} style={{ marginBottom: 12 }} />
                <ControlLabel>City</ControlLabel>
                <FormControl value={shipping.city} onChange={(e) => this.onShippingChange('city', e.target.value)} style={{ marginBottom: 12 }} />
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <ControlLabel>State</ControlLabel>
                        <FormControl value={shipping.state} onChange={(e) => this.onShippingChange('state', e.target.value)} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <ControlLabel>ZIP</ControlLabel>
                        <FormControl value={shipping.zip_code} onChange={(e) => this.onShippingChange('zip_code', e.target.value)} />
                    </div>
                </div>
                <div style={{ marginTop: 16 }}>
                    <Button bsStyle={addressVerified ? 'success' : 'primary'} onClick={this.onValidateAddress.bind(this)} disabled={validatingAddress}>
                        {validatingAddress ? 'Validating...' : addressVerified ? 'Verified' : 'Validate'}
                    </Button>
                </div>
                {validatedAddresses.length > 0 &&
                    <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 4 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>{addressValidationMessage || 'Select the correct shipping address'}</div>
                        {validatedAddresses.map((address, index) =>
                            <label key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8, cursor: 'pointer' }}>
                                <input type="radio" name="dualValidatedAddress" checked={selectedValidatedAddressIndex === index} onChange={() => this.onSelectValidatedAddress(index)} style={{ marginRight: 8, marginTop: 3 }} />
                                <span>{formatValidatedAddressLabel(address)}</span>
                            </label>
                        )}
                    </div>
                }
                {addressValidationMessage && validatedAddresses.length === 0 &&
                    <div style={{ marginTop: 12, padding: 10, background: 'rgb(240,249,243)', border: '1px solid rgb(109,173,130)', borderRadius: 4 }}>{addressValidationMessage}</div>
                }
                {addressValidationError && <div style={{ marginTop: 12, color: '#a94442' }}>{addressValidationError}</div>}
            </div>
        );
    }

    renderStep4() {
        const { patient } = this.props;
        const {
            rpmDeclined,
            ccmConsentType,
            rpmConsentChecked,
            rpmConsentType,
            shipping,
            submitError,
            submitting,
            connectionType,
            mobile,
        } = this.state;
        const selectedDevice = this.getSelectedDevice();
        const ccmConsentOption = RPM_CONSENT_TYPE_OPTIONS.find((option) => option.value === ccmConsentType);
        const rpmConsentOption = RPM_CONSENT_TYPE_OPTIONS.find((option) => option.value === rpmConsentType);
        const programLabel = this.getProgramShortLabel();

        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 4 — confirm &amp; enroll</div>
                <div style={boxStyle}>
                    <div><span style={{ fontWeight: 600 }}>Patient:</span> {patient.first_name} {patient.last_name} · CCM ID {patient.id}</div>
                    <div><span style={{ fontWeight: 600 }}>{programLabel} Consent Type:</span> {ccmConsentOption ? ccmConsentOption.label : '—'}</div>
                    {!rpmDeclined &&
                        <div><span style={{ fontWeight: 600 }}>Device:</span> {this.getDeviceTypeLabel()} · {this.getConnectionLabel()} · {selectedDevice ? selectedDevice.description : '—'}</div>
                    }
                    {!rpmDeclined && connectionType === 'bluetooth' &&
                        <div><span style={{ fontWeight: 600 }}>Mobile Phone:</span> {mobile ? numberToPhone(mobile) : '—'}</div>
                    }
                    {!rpmDeclined &&
                        <div>
                            <span style={{ fontWeight: 600 }}>Ship To:</span>
                            {' '}{shipping.address1}{shipping.address2 ? `, ${shipping.address2}` : ''}, {shipping.city}, {shipping.state} {shipping.zip_code}
                        </div>
                    }
                </div>
                {!rpmDeclined &&
                    <div style={{ background: 'rgb(240,249,243)', border: '1px solid rgb(109,173,130)', borderRadius: 4, padding: 16 }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: 12 }}>
                            <input type="checkbox" checked={rpmConsentChecked} onChange={(e) => this.setState({ rpmConsentChecked: e.target.checked })} style={{ marginRight: 8, marginTop: 3 }} />
                            <span>Patient has verbally consented to RPM enrollment.</span>
                        </label>
                        <ControlLabel>RPM Consent Type</ControlLabel>
                        <FormControl componentClass="select" value={rpmConsentType} onChange={(e) => this.setState({ rpmConsentType: e.target.value })}>
                            <option value="">Select consent type</option>
                            {RPM_CONSENT_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </FormControl>
                    </div>
                }
                {submitError && <div style={{ color: '#a94442', marginTop: 12 }}>{submitError}</div>}
                {submitting && <div style={{ marginTop: 12, color: '#666' }}>Submitting enrollment...</div>}
                {!rpmDeclined && rpmConsentOption && <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>RPM consent: {rpmConsentOption.label}</div>}
            </div>
        );
    }

    renderStep5() {
        const { patient } = this.props;
        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Part 3 — schedule first call</div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>
                    Book {patient.first_name}&apos;s first care-coordination call.
                </div>
                <ScheduleNextCallModal
                    isClinical={false}
                    isCne
                    patient={patient}
                    onScheduleDateSelected={(date) => this.setState({ scheduleNextCallDate: date })}
                    onScheduleSlotSelected={(slot) => this.setState({ scheduleNextCallSlot: slot })}
                    scheduleNextCallSlot={this.state.scheduleNextCallSlot}
                    scheduleNextCallDate={this.state.scheduleNextCallDate}
                />
            </div>
        );
    }

    renderComplete() {
        const { patient } = this.props;
        const { workItemText, rpmDeclined } = this.state;
        const programLabel = this.getProgramShortLabel();

        return (
            <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                <div style={{ fontSize: 48, color: '#5cb85c', marginBottom: 12 }}>✓</div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
                    {rpmDeclined ? `${programLabel} enrollment complete` : `${programLabel} + RPM enrollment complete`}
                </div>
                <div style={{ color: '#666', marginBottom: 16 }}>
                    {patient.first_name}&apos;s {rpmDeclined ? `${programLabel} enrollment` : `${programLabel} enrollment and RPM Add-On`} {rpmDeclined ? 'is confirmed' : 'are confirmed with Validic'}, and the first care call is scheduled.
                </div>
                {workItemText &&
                    <div style={{ textAlign: 'left', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 4, padding: 12 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Chart work-item recorded</div>
                        <div style={{ fontStyle: 'italic' }}>{workItemText}</div>
                    </div>
                }
            </div>
        );
    }

    renderFooter() {
        const {
            step,
            showComplete,
            rpmDeclined,
            ccmConsentType,
            ccmConsentChecked,
            okToText,
            selectedDeviceId,
            connectionType,
            addressVerified,
            rpmConsentChecked,
            rpmConsentType,
            submitting,
            scheduleNextCallSlot,
        } = this.state;
        const textClinical = this.getTextClinicalEnabled();
        const programLabel = this.getProgramShortLabel();

        if (showComplete) {
            return (
                <div style={{ borderTop: '1px solid #ddd', paddingTop: 15, marginTop: 10, textAlign: 'right' }}>
                    <Button bsStyle="primary" onClick={this.onClose.bind(this)}>Done</Button>
                </div>
            );
        }

        const step1Disabled = !ccmConsentType || !ccmConsentChecked || (textClinical && okToText == null) || !this.getMobileValue();
        const step2Disabled = !selectedDeviceId || !connectionType || (
            connectionType === 'bluetooth' && !this.getBluetoothMobileValue()
        );
        const step4Disabled = rpmDeclined ? submitting : (!rpmConsentChecked || !rpmConsentType || submitting);
        const step5Disabled = !scheduleNextCallSlot;

        return (
            <div style={{ borderTop: '1px solid #ddd', paddingTop: 15, marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    {step === 1 && <Button onClick={this.onClose.bind(this)}>Cancel</Button>}
                    {step > 1 && step < 6 && <Button onClick={this.onBack.bind(this)}>Back</Button>}
                    {step === 1 &&
                        <Button bsStyle="danger" style={{ marginLeft: 8, background: '#fff', color: '#d9534f', borderColor: '#d9534f' }} onClick={this.props.onDeclineCcm}>
                            Decline {programLabel}
                        </Button>
                    }
                    {step === 2 &&
                        <Button bsStyle="danger" style={{ marginLeft: 8, background: '#fff', color: '#d9534f', borderColor: '#d9534f' }} onClick={this.onDeclineRpm.bind(this)}>
                            Decline RPM
                        </Button>
                    }
                </div>
                <div>
                    {step === 1 && <Button bsStyle="primary" disabled={step1Disabled} onClick={this.onContinueFromStep1.bind(this)}>Continue</Button>}
                    {step === 2 && <Button bsStyle="primary" disabled={step2Disabled} onClick={this.onContinueFromStep2.bind(this)}>Continue</Button>}
                    {step === 3 && <Button bsStyle="primary" disabled={!addressVerified} onClick={() => this.goToStep(4)}>Continue</Button>}
                    {step === 4 && <Button bsStyle="success" disabled={step4Disabled} onClick={this.onSubmitEnrollment.bind(this)}>Confirm &amp; Enroll</Button>}
                    {step === 5 && <Button bsStyle="success" disabled={step5Disabled} onClick={this.onCompleteEnrollment.bind(this)}>Complete Enrollment</Button>}
                </div>
            </div>
        );
    }

    render() {
        const { show, patient } = this.props;
        const { step, showComplete, showRpmDeclineModal } = this.state;

        const programLabel = this.getProgramShortLabel();

        if (!show) {
            return null;
        }

        return (
            <div>
                <Modal bsSize="large" show={show && !showRpmDeclineModal} onHide={this.onClose.bind(this)} backdrop="static">
                    <Modal.Header closeButton onHide={this.onClose.bind(this)}>
                        <Modal.Title>
                            <div className="text-align-center">
                                {showComplete ? 'Enrollment Complete' : `New Patient — ${programLabel} + RPM Enrollment`}
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {!showComplete && this.renderStepper()}
                        {showComplete && this.renderComplete()}
                        {!showComplete && step === 1 && this.renderStep1()}
                        {!showComplete && step === 2 && this.renderStep2()}
                        {!showComplete && step === 3 && this.renderStep3()}
                        {!showComplete && step === 4 && this.renderStep4()}
                        {!showComplete && step === 5 && this.renderStep5()}
                        {this.renderFooter()}
                    </Modal.Body>
                </Modal>
                <RpmAddonDeclineModal
                    show={show && showRpmDeclineModal}
                    onHide={this.onRpmDeclineBack.bind(this)}
                    onBack={this.onRpmDeclineBack.bind(this)}
                    onDeclineSuccess={this.onRpmDeclineSuccess.bind(this)}
                    patient={patient}
                />
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    adminActions: bindActionCreators(adminActionCreator, dispatch),
});

export default connect(null, mapDispatchToProps)(RpmDualEnrollmentModal);
